import { z } from 'zod';

/**
 * Die Bedarfsvorschau, wie ein **Lieferant** sie im Partnerportal sieht
 * (ADR 0085, Stufe 1).
 *
 * Das ist bewusst NICHT `ForecastLineResponse`, sondern eine **Positivliste**:
 * Ein neues internes Feld darf hier nicht automatisch mitwachsen, wie es ein
 * `omit()` täte.
 *
 * Nicht enthalten sind:
 *
 * * `note` — interne Bemerkung zur Position (Preise, Verhandlungsstand,
 *   Bewertungen von Lieferanten). Geht niemanden von außen etwas an.
 * * der **Name der Bedarfsliste** — Listen heißen im Alltag nach Kunde oder
 *   Vertriebsstand; der Partner braucht ihn nicht.
 *
 * `projectText` ist dagegen seit 2026-08-07 **ausdrücklich freigegeben**
 * (Entscheidung Tech-Lead): Ein Lieferant, der weiß, für welches Projekt eine
 * Menge bestimmt ist, kann Teillieferungen richtig zuordnen und bei Rückfragen
 * darauf verweisen. Der Preis dafür ist bekannt und akzeptiert — die
 * Projekt-Referenz ist Freitext (ADR 0035) und nennt in der Praxis Kunden- und
 * Anlagennamen. Ein Lieferant erfährt damit, für wen wir bauen. Wer das für
 * eine einzelne Liste nicht will, gibt sie schlicht nicht frei.
 */
export const portalForecastRowSchema = z.object({
  /** Artikel-Id — nur als stabiler Schlüssel für die Anzeige. */
  articleId: z.string().uuid(),
  articleNumber: z.string().nullable(),
  description: z.string(),
  unit: z.string().nullable(),
  /**
   * Projekt-Referenz der Bedarfszeile (Freitext, ADR 0035); `null`, wenn keine
   * hinterlegt ist. Bewusst freigegeben — siehe Kopfkommentar.
   */
  projectText: z.string().nullable(),
  /** Summe über alle freigegebenen Listen für genau diesen Termin. */
  quantity: z.number(),
  /** Voraussichtlicher Liefertermin; `null` = ohne Termin geplant. */
  expectedDate: z.string().datetime().nullable(),
});
export type PortalForecastRow = z.infer<typeof portalForecastRowSchema>;

/**
 * Antwort der Portal-Bedarfsvorschau. `hasSupplierLink` unterscheidet die zwei
 * Fälle, die sonst beide „leere Liste" wären: Der Zugang ist keinem Lieferanten
 * zugeordnet (dann kann gar nichts erscheinen — ein Fall für uns), oder es gibt
 * schlicht gerade keinen freigegebenen Bedarf (normal).
 */
export const portalForecastResponseSchema = z.object({
  rows: z.array(portalForecastRowSchema),
  hasSupplierLink: z.boolean(),
});
export type PortalForecastResponse = z.infer<typeof portalForecastResponseSchema>;
